import { useMemo } from 'react';
import type { Scenario } from '../types';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';

export type DurationOption = { value: number; label: string };

export interface SceneTimeline {
  /** 絵コンテのコマ尺セレクトの選択肢 (1秒〜シナリオ想定尺)。 */
  storyboardCutDurationOptions: DurationOption[];
  /** このシーンのコマ尺の表示値 (選択肢上限でクランプ済み)。 */
  storyboardCutValue: number;
  /** 任意シーンの実効尺 (ブランドカットは紐付け動画の実尺 > 表示秒数)。 */
  getSceneEffectiveDuration: (s: Scenario['scenes'][number]) => number;
  /** タイムライン上でこのシーンが始まる秒数 (ストーリーボードモード以外は 0)。 */
  storyboardStartSeconds: number;
  /** タイムライン上でこのシーンが終わる秒数。 */
  storyboardEndSeconds: number;
  /** ブランドカットに動画素材が紐付いているか (尺が実尺固定になる)。 */
  brandCutHasBoundVideo: boolean;
  /** 秒数を M:SS 形式に整形。 */
  formatTimecode: (totalSeconds: number) => string;
  /** videoBoxOnly の「尺」選択肢 (対象外のときは null)。 */
  storyboardDurationOptions: DurationOption[] | null;
  /** タイムライン総尺をカバーできる最小の対応尺 (未指定シーンの初期値)。 */
  storyboardDefaultDuration: number | null;
  /** 「特定シーンだけ生成」の開始〜終了シーン合計尺と、丸めた尺。 */
  getStoryboardRangeDuration: (startOrder: number, endOrder: number) => { totalSeconds: number; duration: number };
}

/**
 * シーンカードのタイムライン系の派生値をまとめて計算する。
 * DOM を持たない純粋な導出のみ (state 更新は呼び出し側の SceneItem に残す)。
 */
export function useSceneTimeline({
  scenario,
  scene,
  sceneIndex,
  sceneKey,
  videoBoxOnly,
  availableDurations,
}: {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  sceneIndex: number;
  sceneKey: string;
  videoBoxOnly: boolean;
  availableDurations: number[];
}): SceneTimeline {
  const { videoGenerationMode, getSceneStoryboardDuration } = useSceneHandlers();
  const { sceneVideoMaterialId } = useSceneStateContext();

  // シナリオの想定尺（"15秒" のような文字列から数値抽出）
  const scenarioIntendedDuration = useMemo<number | null>(() => {
    const meta = scenario.generation_metadata as Record<string, unknown> | undefined;
    const durationStr = meta?.duration;
    if (typeof durationStr !== 'string') return null;
    const m = /(\d+)/.exec(durationStr);
    if (!m) return null;
    const n = Number(m[1]);
    return n > 0 ? n : null;
  }, [scenario]);
  // 絵コンテのコマ尺セレクトの選択肢: 1秒〜「シナリオ設定の想定尺」までの整数秒。
  // 想定尺が不明な場合は従来の上限 3 秒にフォールバック。
  const storyboardCutDurationOptions = useMemo(() => {
    const max = scenarioIntendedDuration && scenarioIntendedDuration > 0 ? scenarioIntendedDuration : 3;
    return Array.from({ length: max }, (_, i) => ({ value: i + 1, label: `${i + 1}秒` }));
  }, [scenarioIntendedDuration]);
  // 表示値は選択肢の上限を超えないようにクランプ (想定尺を縮めた場合の保険)。
  const storyboardCutMax = storyboardCutDurationOptions[storyboardCutDurationOptions.length - 1]?.value ?? 3;
  const storyboardCutValue = Math.min(getSceneStoryboardDuration(sceneKey, scene.storyboard_duration_seconds ?? undefined), storyboardCutMax);
  // シーンの実効尺。タイムライン表示を実際のエクスポート長 (mp4_composer) と一致させる。
  // ブランドカット (タイトルカード/エンドカード) は:
  //   - 動画素材が紐付いていればその実尺を尊重 (固定秒数で切り詰めない)。実尺が不明な
  //     間は brand_cut_duration_seconds (既定3秒) にフォールバック (backend は実ファイル
  //     を probe して補完する)。
  //   - 静止カードのみなら brand_cut_duration_seconds。
  // それ以外の通常シーンはコマ尺を採用する。
  const getSceneEffectiveDuration = (s: Scenario['scenes'][number]) => {
    if (s.brand_cut_position) {
      const boundVideoDur = s.video_material_id
        ? (s.video_duration_seconds
            || ((s.generation_metadata as any)?.duration_seconds as number | undefined)
            || 0)
        : 0;
      if (boundVideoDur > 0) return boundVideoDur;
      return s.brand_cut_duration_seconds ?? 3;
    }
    return Math.min(
      getSceneStoryboardDuration(`${scenario.id}-scene-${s.order}`, s.storyboard_duration_seconds ?? undefined),
      storyboardCutMax,
    );
  };
  // このシーンがタイムライン上で始まる/終わる秒数。シーン1 を 0 秒起点とし、
  // 先行する全シーンの実効尺を累積する (例: 0秒〜2秒, 2秒〜5秒...)。
  let storyboardStartSeconds = 0;
  if (videoGenerationMode === 'storyboard') {
    for (let i = 0; i < sceneIndex; i += 1) {
      const prev = scenario.scenes[i];
      if (!prev) continue;
      storyboardStartSeconds += getSceneEffectiveDuration(prev);
    }
  }
  const storyboardEndSeconds = storyboardStartSeconds + getSceneEffectiveDuration(scene);
  // ブランドカットに動画素材が紐付いている場合、出力尺はその動画の実尺で決まり
  // (mp4_composer 参照)、ブランドカット表示秒数のステッパーは効かない。混乱を避けるため
  // ステッパーは出さず、実効尺を読み取り専用で示す。
  const brandCutHasBoundVideo =
    !!scene.brand_cut_position && (!!scene.video_material_id || !!sceneVideoMaterialId[sceneKey]);
  // 全シーンの実効尺合計 = ストーリーボードのタイムライン総尺。動画クリップ尺(尺)の
  // 既定値の基準にする (想定尺は使わない)。
  let storyboardTotalSeconds = 0;
  if (videoGenerationMode === 'storyboard') {
    for (const s of scenario.scenes) {
      storyboardTotalSeconds += getSceneEffectiveDuration(s);
    }
  }
  // 秒数を M:SS 形式に整形 (例: 0 → 0:00, 65 → 1:05)
  // 動画素材の実尺など小数秒が混じっても M:SS で表示できるよう秒は丸める。
  const formatTimecode = (totalSeconds: number) => {
    const rounded = Math.round(totalSeconds);
    const m = Math.floor(rounded / 60);
    const s = rounded % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  // ストーリーボードモードかつ videoBoxOnly のときだけ、「尺」を可変にする。
  // 動画は 1 本だが、シーケンスでは尺がタイムライン総尺を超えても固定にせず、
  // モデルが対応する全尺 (例: Seedance なら 4〜15秒) から自由に選べるようにする。
  // -1 (auto) は UI で「-1秒」と表示されておかしいので常に除外。
  const storyboardDurationOptions = useMemo(() => {
    if (!(videoGenerationMode === 'storyboard' && videoBoxOnly)) return null;
    const sorted = [...availableDurations].sort((a, b) => a - b).filter((d) => d > 0);
    return sorted.map((v) => ({ value: v, label: `${v}秒` }));
  }, [videoGenerationMode, videoBoxOnly, availableDurations]);
  // 既定の尺: タイムライン総尺をカバーできる最小の対応尺。総尺がモデル上限を超える
  // 場合は上限。ユーザーが未指定 (未保存) のシーンの初期値に使う。
  const storyboardDefaultDuration = useMemo(() => {
    if (!storyboardDurationOptions || storyboardDurationOptions.length === 0) return null;
    const values = storyboardDurationOptions.map((o) => o.value);
    return values.find((d) => d >= storyboardTotalSeconds) ?? values[values.length - 1];
  }, [storyboardDurationOptions, storyboardTotalSeconds]);
  // 「特定シーンだけ生成」: 開始〜終了シーンの実効尺を合計し、その合計をカバーできる
  // 最小の対応尺に丸めて返す。ブランドカット (タイトル/エンドカード) は本編動画に
  // 含まれないため範囲計算から除外する。totalSeconds は素の合計、duration は丸めた尺。
  const getStoryboardRangeDuration = (startOrder: number, endOrder: number): { totalSeconds: number; duration: number } => {
    const lo = Math.min(startOrder, endOrder);
    const hi = Math.max(startOrder, endOrder);
    let totalSeconds = 0;
    for (const s of scenario.scenes) {
      if (s.brand_cut_position) continue;
      if (s.order >= lo && s.order <= hi) totalSeconds += getSceneEffectiveDuration(s);
    }
    const values = (storyboardDurationOptions ?? []).map((o) => o.value);
    const duration = values.length > 0
      ? (values.find((d) => d >= totalSeconds) ?? values[values.length - 1])
      : Math.max(1, Math.round(totalSeconds));
    return { totalSeconds, duration };
  };

  return {
    storyboardCutDurationOptions,
    storyboardCutValue,
    getSceneEffectiveDuration,
    storyboardStartSeconds,
    storyboardEndSeconds,
    brandCutHasBoundVideo,
    formatTimecode,
    storyboardDurationOptions,
    storyboardDefaultDuration,
    getStoryboardRangeDuration,
  };
}
